import { Star, ExternalLink } from "lucide-react";

const REVIEWS = [
  {
    name: "Karen D.",
    initials: "KD",
    color: "#D00017",
    date: "2 weeks ago",
    rating: 5,
    text:
      "Called McFly's on a Saturday morning when our tank was running on fumes. The driver was at our house by early afternoon, super friendly, and the price was exactly what they quoted. Will absolutely use them again.",
    localGuide: true,
  },
  {
    name: "Mike R.",
    initials: "MR",
    color: "#0A1128",
    date: "1 month ago",
    rating: 5,
    text:
      "Switched from a big-name company last winter and couldn't be happier. McFly's rate is consistently fair, drivers are punctual, and I never have to chase them down to schedule. Small, family-run feel — that's what I like.",
  },
  {
    name: "Jen P.",
    initials: "JP",
    color: "#FFD100",
    dark: true,
    date: "3 weeks ago",
    rating: 5,
    text:
      "Woke up to a freezing house on the coldest night of the year. Called McFly's — a real person answered, and a truck was in my driveway within 2 hours. Saved us. Cannot recommend them enough.",
    localGuide: true,
  },
  {
    name: "Tom S.",
    initials: "TS",
    color: "#1A2644",
    date: "1 week ago",
    rating: 5,
    text:
      "Honest pricing, no surprise fees, and the driver was polite and professional. This is exactly what a local heating oil company should feel like. Long Island needs more businesses like this.",
  },
];

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < count ? "fill-[#FBBC04] text-[#FBBC04]" : "text-gray-300"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  return (
    <section id="reviews" className="relative py-24 md:py-32 bg-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            {/* Flyer-style red banner header */}
            <div className="relative inline-block mb-6">
              <div className="bg-brand-red text-white px-6 py-3 border-2 border-brand-navy shadow-[6px_6px_0_#0A1128] -rotate-1">
                <div
                  className="font-heading text-2xl sm:text-3xl tracking-tight"
                  style={{ textShadow: "2px 2px 0 #0A1128" }}
                >
                  WHAT CUSTOMERS <span className="text-brand-gold">SAY</span>
                </div>
              </div>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl leading-[1.05] text-brand-navy" data-testid="reviews-heading">
              5.0 on Google — because<br /> your neighbors are talking.
            </h2>
          </div>

          <div className="md:col-span-5">
            <div className="bg-white border-2 border-brand-navy p-6 shadow-[6px_6px_0_#D00017]" data-testid="reviews-summary">
              <div className="flex items-center gap-4">
                <GoogleG />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-brand-navy/60">Google Reviews</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-heading text-3xl text-brand-navy">5.0</span>
                    <Stars />
                  </div>
                  <div className="text-xs text-brand-navy/70 mt-1">Based on customer feedback</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="reviews-grid">
          {REVIEWS.map((r, i) => (
            <article
              key={i}
              className="bg-white border-2 border-brand-navy p-6 flex flex-col gap-4 shadow-[5px_5px_0_#0A1128] hover:shadow-[9px_9px_0_#D00017] hover:-translate-x-1 hover:-translate-y-1 transition-all"
              data-testid={`review-${i}`}
            >
              <header className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full border-2 border-brand-navy flex items-center justify-center font-heading text-sm"
                  style={{ background: r.color, color: r.dark ? "#0A1128" : "#fff" }}
                >
                  {r.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-sm text-brand-navy leading-tight truncate">{r.name}</div>
                  <div className="text-[10px] text-brand-navy/60 uppercase tracking-wider">
                    {r.localGuide ? "Local Guide" : "Reviewer"} · {r.date}
                  </div>
                </div>
                <GoogleG small />
              </header>
              <Stars count={r.rating} />
              <p className="text-sm text-brand-navy/80 leading-relaxed flex-1">{r.text}</p>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://www.google.com/maps/search/McFly%27s+Fuel+Inc+Long+Island"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white hover:bg-brand-gold text-brand-navy px-6 py-3.5 font-heading tracking-wide border-2 border-brand-navy shadow-[5px_5px_0_#0A1128] hover:shadow-[8px_8px_0_#0A1128] hover:-translate-x-1 hover:-translate-y-1 transition-all"
            data-testid="see-reviews-btn"
          >
            <GoogleG small /> See all reviews on Google
            <ExternalLink size={14} />
          </a>
          <a
            href="#order"
            className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redDark text-white px-6 py-3.5 font-heading tracking-wide border-2 border-brand-navy shadow-[5px_5px_0_#0A1128] hover:shadow-[8px_8px_0_#0A1128] hover:-translate-x-1 hover:-translate-y-1 transition-all"
            data-testid="reviews-order-cta"
          >
            Join Our Happy Customers
          </a>
        </div>
      </div>
    </section>
  );
}

function GoogleG({ small }) {
  const size = small ? 20 : 40;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden className="shrink-0">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
